
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from '@/components/ui/input';
import { Textarea } from "@/components/ui/textarea";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  Calendar, 
  User, 
  Tag, 
  CheckSquare, 
  Settings, 
  ChevronDown, 
  ChevronRight,
  Trash2,
  Save,
  X
} from 'lucide-react';
import { useBoardContext } from '@/contexts/BoardContext';
import { TagPicker } from './TagPicker';
import { DiscreteChecklistManager } from './DiscreteChecklistManager';
import { DateSection } from './card-sections/DateSection';
import { AssignmentSection } from './card-sections/AssignmentSection';
import { AdvancedSection } from './card-sections/AdvancedSection';
import type { Tables } from '@/integrations/supabase/types';
import { useAuth } from '@/hooks/useAuth';
import { useEnhancedCardPresence } from '@/hooks/useEnhancedCardPresence';
import { CardPresenceAvatars } from './CardPresenceAvatars';
import { useToast } from '@/hooks/use-toast';

type CardType = Tables<'cards'> & { assigned_profile?: Tables<'profiles'> };

interface CardDialogProps {
  card: CardType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isCreateMode?: boolean;
}

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
});

export const CardDialog = ({ card, open, onOpenChange, isCreateMode = false }: CardDialogProps) => {
  const { createCard, updateCard, deleteCard, currentBoardId } = useBoardContext();
  const { toast } = useToast();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Expandable sections state
  const [openSections, setOpenSections] = useState({
    dates: false,
    assignment: false,
    tags: false,
    checklists: false,
    advanced: false,
  });

  // Form data state
  const [selectedStartDate, setSelectedStartDate] = useState<Date | undefined>(
    card.start_date ? new Date(card.start_date) : undefined
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>(
    card.end_date ? new Date(card.end_date) : undefined
  );
  const [selectedAssignedUser, setSelectedAssignedUser] = useState<string | null>(
    card.assigned_to || null
  );
  const [selectedTags, setSelectedTags] = useState<string[]>(card.tags || []);
  const [selectedProgressState, setSelectedProgressState] = useState<string>(
    (card as any).progress_state || 'not_started'
  );
  const [category, setCategory] = useState(card.category || '');
  const [archived, setArchived] = useState(card.archived || false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: card.title || '',
      description: card.description || '',
    },
  });

  const { watch, handleSubmit, formState: { isDirty }, reset } = form;

  // Reset dialog state when opening in create mode
  useEffect(() => {
    if (open && isCreateMode) {
      // Reset form
      reset({
        title: '',
        description: '',
      });
      
      // Reset all local state
      setSelectedStartDate(undefined);
      setSelectedEndDate(undefined);
      setSelectedAssignedUser(null);
      setSelectedTags([]);
      setSelectedProgressState('not_started');
      setCategory('');
      setArchived(false);
      setHasUnsavedChanges(false);
      
      // Reset sections state
      setOpenSections({
        dates: false,
        assignment: false,
        tags: false,
        checklists: false,
        advanced: false,
      });
    }
  }, [open, isCreateMode, reset]);

  // Auto-expand sections that have content
  useEffect(() => {
    if (open) {
      const hasDateContent = !!(card.start_date || card.end_date);
      const hasAssignmentContent = !!card.assigned_to;
      const hasTagContent = !!(card.tags && card.tags.length > 0);
      const hasAdvancedContent = !!(card.category || card.archived || (card as any).progress_state !== 'not_started');

      setOpenSections({
        dates: hasDateContent,
        assignment: hasAssignmentContent,
        tags: hasTagContent,
        checklists: false, // Keep closed by default as it's a heavy component
        advanced: hasAdvancedContent,
      });
    }
  }, [open, card]);

  // Track unsaved changes
  useEffect(() => {
    const hasFormChanges = isDirty;
    const hasDateChanges = selectedStartDate !== (card.start_date ? new Date(card.start_date) : undefined) ||
                          selectedEndDate !== (card.end_date ? new Date(card.end_date) : undefined);
    const hasAssignmentChanges = selectedAssignedUser !== card.assigned_to;
    const hasTagChanges = JSON.stringify(selectedTags) !== JSON.stringify(card.tags || []);
    const hasProgressChanges = selectedProgressState !== ((card as any).progress_state || 'not_started');
    const hasCategoryChanges = category !== (card.category || '');
    const hasArchivedChanges = archived !== (card.archived || false);

    setHasUnsavedChanges(
      hasFormChanges || hasDateChanges || hasAssignmentChanges || 
      hasTagChanges || hasProgressChanges || hasCategoryChanges || hasArchivedChanges
    );
  }, [
    isDirty, selectedStartDate, selectedEndDate, selectedAssignedUser, 
    selectedTags, selectedProgressState, category, archived, card
  ]);

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Add local version tracking, used to compare and display up-to-date editing version
  const [cardVersion, setCardVersion] = useState<number>((card as any).version ?? 1);

  // If card.version prop updates (due to underlying change), sync local
  useEffect(() => {
    setCardVersion((card as any).version ?? 1);
  }, [card]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      const cardData = {
        ...data,
        start_date: selectedStartDate ? format(selectedStartDate, 'yyyy-MM-dd') : null,
        end_date: selectedEndDate ? format(selectedEndDate, 'yyyy-MM-dd') : null,
        assigned_to: selectedAssignedUser,
        tags: selectedTags,
        progress_state: selectedProgressState,
        category: category || null,
        archived,
        version: cardVersion, // include the version when updating for optimistic locking
      };

      if (isCreateMode) {
        await createCard({
          ...cardData,
          column_id: card.column_id,
          position: 0,
        });
      } else {
        await updateCard({
          cardId: card.id,
          updates: cardData,
          version: cardVersion, // send for version checking
        });
        // No result check here; rely on error catching below
      }
      setHasUnsavedChanges(false);
      onOpenChange(false);
    } catch (error: any) {
      // If backend throws a conflict error, catch here (fallback if not handled above)
      if (error && /version conflict|conflict/i.test(error.message || '')) {
        toast({
          title: 'Edit Conflict',
          description: 'Someone else updated this card before you. Please reload to get the latest version.',
          variant: 'destructive'
        });
        setIsLoading(false);
        return;
      }
      console.error('Error saving card:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (isCreateMode) return;
    
    setIsLoading(true);
    try {
      await deleteCard(card.id);
      setShowDeleteDialog(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Error deleting card:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    onOpenChange(false);
  };

  // Count items in each section for badges
  const sectionCounts = {
    dates: (selectedStartDate ? 1 : 0) + (selectedEndDate ? 1 : 0),
    assignment: selectedAssignedUser ? 1 : 0,
    tags: selectedTags.length,
    advanced: (category ? 1 : 0) + (selectedProgressState !== 'not_started' ? 1 : 0) + (archived ? 1 : 0),
  };

  const { user } = useAuth();
  
  // Use the enhanced card presence hook
  const { setEditing } = useEnhancedCardPresence({
    cardId: card.id,
    boardId: currentBoardId || "",
    userId: user?.id ?? "",
    userName: user?.email ?? user?.user_metadata?.full_name ?? "",
    status: open ? "editing" : "viewing"
  });

  // Set presence status to editing only while dialog is open
  useEffect(() => {
    setEditing(open);
    // On unmount or close, revert to viewing
    return () => setEditing(false);
  }, [open, setEditing]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleCancel}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                {isCreateMode ? 'Create New Card' : 'Edit Card'}
                {hasUnsavedChanges && (
                  <Badge variant="secondary" className="text-xs">
                    Unsaved changes
                  </Badge>
                )}
              </DialogTitle>
              <CardPresenceAvatars 
                cardId={card.id}
                currentStatus="editing"
                small 
              />
            </div>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Always visible: Title and Description */}
              <div className="space-y-4 pb-4 border-b">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter card title" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Enter card description" rows={2} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Expandable Sections */}
              <div className="space-y-2">
                {/* Dates Section */}
                <Collapsible open={openSections.dates} onOpenChange={() => toggleSection('dates')}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-3 h-auto">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>Dates</span>
                        {sectionCounts.dates > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {sectionCounts.dates}
                          </Badge>
                        )}
                      </div>
                      {openSections.dates ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="px-3 pb-3">
                    <DateSection
                      startDate={selectedStartDate}
                      endDate={selectedEndDate}
                      onStartDateChange={setSelectedStartDate}
                      onEndDateChange={setSelectedEndDate}
                    />
                  </CollapsibleContent>
                </Collapsible>

                {/* Assignment Section */}
                <Collapsible open={openSections.assignment} onOpenChange={() => toggleSection('assignment')}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-3 h-auto">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>Assignment</span>
                        {sectionCounts.assignment > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {sectionCounts.assignment}
                          </Badge>
                        )}
                      </div>
                      {openSections.assignment ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="px-3 pb-3">
                    <AssignmentSection
                      assignedUserId={selectedAssignedUser}
                      onAssignedUserChange={setSelectedAssignedUser}
                    />
                  </CollapsibleContent>
                </Collapsible>

                {/* Tags Section */}
                <Collapsible open={openSections.tags} onOpenChange={() => toggleSection('tags')}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-3 h-auto">
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4" />
                        <span>Tags</span>
                        {sectionCounts.tags > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {sectionCounts.tags}
                          </Badge>
                        )}
                      </div>
                      {openSections.tags ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="px-3 pb-3">
                    <TagPicker
                      selectedTags={selectedTags}
                      onTagsChange={setSelectedTags}
                    />
                  </CollapsibleContent>
                </Collapsible>

                {/* Checklists Section */}
                
                  <Collapsible open={openSections.checklists} onOpenChange={() => toggleSection('checklists')}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between p-3 h-auto">
                        <div className="flex items-center gap-2">
                          <CheckSquare className="h-4 w-4" />
                          <span>Checklists</span>
                        </div>
                        {openSections.checklists ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="px-3 pb-3">
                      <DiscreteChecklistManager cardId={card.id} />
                    </CollapsibleContent>
                  </Collapsible>
                

                {/* Advanced Section */}
                <Collapsible open={openSections.advanced} onOpenChange={() => toggleSection('advanced')}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-3 h-auto">
                      <div className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        <span>Advanced</span>
                        {sectionCounts.advanced > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {sectionCounts.advanced}
                          </Badge>
                        )}
                      </div>
                      {openSections.advanced ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="px-3 pb-3">
                    <AdvancedSection
                      progressState={selectedProgressState}
                      category={category}
                      archived={archived}
                      onProgressStateChange={setSelectedProgressState}
                      onCategoryChange={setCategory}
                      onArchivedChange={setArchived}
                    />
                  </CollapsibleContent>
                </Collapsible>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  {!isCreateMode && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => setShowDeleteDialog(true)}
                      disabled={isLoading}
                      className="sm:mr-auto"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  )}
                  <div className="flex gap-2 sm:ml-auto">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                      <Save className="h-4 w-4 mr-2" />
                      {isCreateMode ? 'Create' : 'Save'}
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Card</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{card.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
